import { getSortComparator } from 'BMapsSrc/util/js_utils';
import { getPropertyTooltip } from './properties_rendering';

export const ClassificationTypes = {
    BINARY: 'Binary',
    REGRESSION: 'Regression',
};

export const Directionality = {
    NOT_APPLICABLE: 'N/A',
    MINIMIZE: 'minimize',
    MAXIMIZE: 'maximize',
};

export const DirectionalityStrings = {
    NOT_APPLICABLE: 'N/A',
    PreferNegative: 'More negative is better',
    PreferPositive: 'More positive is better',
    PreferLower: 'Lower is better',
    PreferHigher: 'Higher is better',
    PreferZero: 'Closer to 0.0 is better',
    PreferOne: 'Closer to 1.0 is better',
    PreferZeroPct: 'Closer to 0% is better',
    PreferHundredPct: 'Closer to 100% is better',
};

/**
 * Unit type classification for determining directionality display strings.
 * - REAL: Can be negative (energy, log values) → "More negative/positive is better"
 * - POSITIVE: Always positive (IC50, distance) → "Lower/Higher is better"
 * - GTE_ZERO: Always positive, 0 is ideal (variance) → "Closer to 0.0 is better"
 * - PROBABILITY: 0-1 score → "Closer to 0.0/1.0 is better"
 * - PERCENTAGE: 0-100% → "Closer to 0%/100% is better"
 */
export const UnitType = {
    REAL: 'real',
    POSITIVE: 'positive',
    GTE_ZERO: 'gteZero',
    PROBABILITY: 'probability',
    PERCENTAGE: 'percentage',
};

/**
 * Get the display string for a directionality, considering the unit type.
 * @param {string} directionality - One of Directionality values
 * @param {string} unitType - One of UnitType values (optional, falls back to unit string matching)
 * @param {string} unit - The unit string (used for fallback matching if unitType not provided)
 * @returns {string} Human-readable directionality string
 */
export function getDirectionalityString(directionality, unitType, unit) {
    // Determine effective unit type from explicit unitType or by matching unit string
    let effectiveUnitType = unitType;
    if (!effectiveUnitType && unit) {
        if (unit === 'Probability' || unit === 'Score (0-1)') {
            effectiveUnitType = UnitType.PROBABILITY;
        } else if (unit === '%' || unit === 'Percentage') {
            effectiveUnitType = UnitType.PERCENTAGE;
        }
    }

    if (directionality === Directionality.MINIMIZE) {
        switch (effectiveUnitType) {
            case UnitType.PROBABILITY:
            case UnitType.GTE_ZERO:
                return DirectionalityStrings.PreferZero;
            case UnitType.PERCENTAGE:
                return DirectionalityStrings.PreferZeroPct;
            case UnitType.POSITIVE:
                return DirectionalityStrings.PreferLower;
            default:
                return DirectionalityStrings.PreferNegative;
        }
    } else if (directionality === Directionality.MAXIMIZE) {
        switch (effectiveUnitType) {
            case UnitType.PROBABILITY:
                return DirectionalityStrings.PreferOne;
            case UnitType.PERCENTAGE:
                return DirectionalityStrings.PreferHundredPct;
            case UnitType.POSITIVE:
            case UnitType.GTE_ZERO:
                return DirectionalityStrings.PreferHigher;
            default:
                return DirectionalityStrings.PreferPositive;
        }
    }
    return DirectionalityStrings.NOT_APPLICABLE;
}

export const ScaleTypes = {
    LINEAR: 'linear',
    LOGARITHMIC: 'logarithmic',
};

export const ValueFormatTypes = {
    DECADE: 'decade',
    TENTHS: 'tenths',
    HUNDREDTHS: 'hundredths',
    THOUSANDTHS: 'thousandths',
    WHOLE_NUMBER: 'wholeNumber',
};

/**
 * Return a sort function based on a property directionality.
 * Importantly, this will sort empty values correctly.
 * Unspecified or N/A directionality will sort empty values low.
 * @param {*} directionality
 * @returns {function}
 */
export function getSortComparatorForDirectionality(directionality) {
    const emptyValueSortResult = directionality === Directionality.MINIMIZE ? 1 : -1;
    return getSortComparator(emptyValueSortResult);
}

/**
 * Use for MUI DataGrid `sortingOrder` column property
 * @param {*} directionality
 * @returns {Array<string>}
 */
export function getSortingOrderForDirectionality(directionality) {
    return directionality === Directionality.MINIMIZE ? ['asc', 'desc', null] : ['desc', 'asc', null];
}

/**
 * Prepare property group info for ScopedProperty global metadata.
 * @param {{ groupMeta: { label: string, propApplicability },
 * propertyInfo: { [propName: string]: {
 * label, description, unit, directionality, range, goodRange, r2, classificationType
 * }}}}
 * @returns {{ metadata, properties: { metadata: { label, description, tooltip } } }}
 */
export function preparePropertyGroupMetadata({ groupMeta, propertyInfo, tooltipHeading }) {
    return {
        metadata: groupMeta,
        properties: Object.entries(propertyInfo).reduce((acc, [propName, propInfo]) => ({
            ...acc,
            [propName]: {
                metadata: {
                    label: propInfo.label,
                    description: propInfo.description,
                    tooltip: getPropertyTooltip(tooltipHeading, propInfo),
                },
            },
        }), {}),
    };
}
